import { createEventHook } from '@vueuse/core';
import { uid } from 'quasar';
import type { KountSdk, KountSdkCallbackParams, KountSdkConfig } from '~/types/kount';

export default defineNuxtPlugin({
  name: 'kygunco:kount',
  dependsOn: [],
  parallel: true,
  setup: () => {
    const config = useRuntimeConfig();

    const enabled = config.public.kount.enabled;
    const clientId = config.public.kount.clientId;
    const hostname = config.public.kount.hostname;
    const debug = config.public.kount.debug;

    const sessionId = ref('');
    const ready = ref(false);
    const sdk = ref<KountSdk | null>(null);

    const onCollectBegin = createEventHook<KountSdkCallbackParams>();
    const onCollectEnd = createEventHook<KountSdkCallbackParams>();

    const generateSessionId = () => uid().replaceAll('-', '');

    onCollectBegin.on(() => ready.value = false);
    onCollectEnd.on(() => ready.value = true);

    const load = usePromiseLock(async () => {
      if (!enabled) {
        return;
      }

      if (!import.meta.client) {
        return;
      }

      sessionId.value = generateSessionId();

      // If SDK already loaded, renew session
      if (sdk.value) {
        sdk.value.NewSession(sessionId.value);
        return;
      }

      // Load SDK only when needed
      const { default: kountSDK } = await import('@kount/kount-web-client-sdk/kount-web-client-sdk');

      const config: KountSdkConfig = {
        clientID: clientId,
        hostname: hostname,
        isSinglePageApp: true,
        isDebugEnabled: debug,
        callbacks: {
          'collect-begin': onCollectBegin.trigger,
          'collect-end': onCollectEnd.trigger,
        },
      };

      sdk.value = kountSDK(config, sessionId.value);
    });

    return {
      provide: {
        kount: {
          load,
          enabled,
          ready,
          sessionId,
          onCollectBegin: onCollectBegin.on,
          onCollectEnd: onCollectEnd.on,
        },
      },
    };
  },
});
